import { ref, watch, nextTick, type Ref, type ComputedRef } from 'vue'

type BoolRef = Ref<boolean> | ComputedRef<boolean>

export interface SearchCollectionHydrationOptions {
  isSearchMode: BoolRef
  isLoadingCollections: BoolRef
  isLoadingMoreCollections: BoolRef
  hasMoreCollections: BoolRef
  missingSearchCollectionIds: ComputedRef<number[]>
  loadMoreCollections: () => Promise<void>
}

export interface SearchCollectionHydrationState {
  isHydratingSearchCollections: Ref<boolean>
  hydrateSearchCollections: () => Promise<void>
}

export function useSearchCollectionHydration(
  options: SearchCollectionHydrationOptions
): SearchCollectionHydrationState {
  const isHydratingSearchCollections = ref(false)

  async function hydrateSearchCollections(): Promise<void> {
    if (!options.isSearchMode.value) return
    if (isHydratingSearchCollections.value) return
    if (options.isLoadingCollections.value) return
    if (options.isLoadingMoreCollections.value) return

    isHydratingSearchCollections.value = true

    try {
      while (
        options.missingSearchCollectionIds.value.length > 0 &&
        options.hasMoreCollections.value
      ) {
        await options.loadMoreCollections()
        await nextTick()
      }
    } finally {
      isHydratingSearchCollections.value = false
    }
  }

  watch(
    options.missingSearchCollectionIds,
    async (missingIds) => {
      if (!options.isSearchMode.value) return
      if (missingIds.length === 0) return

      await hydrateSearchCollections()
    },
    { immediate: true }
  )

  return {
    isHydratingSearchCollections,
    hydrateSearchCollections
  }
}